import { ColumnManager } from '../../utils/ColumnManager'

const PRICE_GROUP_META_FIELDS = [
  'shipping_mark',
  'grade',
  'size',
  'remain_quantity',
  'total_quantity',
  'package_quantity',
  'listing_quantity',
  'quantity_unit'
]

const normalizePriceGroup = (rawGroup = {}, parentItem = {}) => {
  const distribution_result = Array.isArray(rawGroup.distribution_result)
    ? rawGroup.distribution_result
    : rawGroup.distribution_result
      ? Object.entries(rawGroup.distribution_result)
          .filter(([k]) => k !== 'price' && k !== 'id')
          .map(([customer_code, delivery_quantity]) => ({
            customer_code,
            delivery_quantity: Number(delivery_quantity) || 0
          }))
      : []

  const normalized = {
    input_price: rawGroup.input_price ?? '',
    output_price: rawGroup.output_price ?? '',
    profit: rawGroup.profit ?? '',
    distribution_result,
    _priceId:
      rawGroup._priceId ||
      `p_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
  }

  PRICE_GROUP_META_FIELDS.forEach((key) => {
    if (rawGroup[key] !== undefined && rawGroup[key] !== null) {
      normalized[key] = rawGroup[key]
    } else if (parentItem[key] !== undefined && parentItem[key] !== null) {
      normalized[key] = parentItem[key]
    } else if (
      key === 'remain_quantity' ||
      key === 'total_quantity' ||
      key === 'package_quantity' ||
      key === 'listing_quantity' ||
      key === 'quantity_unit' 
    ) {
      normalized[key] = 0
    } else {
      normalized[key] = ''
    }
  })

  if (!normalized.listing_quantity && normalized.listing_quantity !== 0) {
    if (parentItem.listing_quantity !== undefined) {
      normalized.listing_quantity = parentItem.listing_quantity
    }
  }

  if (
    rawGroup.quantity !== undefined &&
    rawGroup.quantity !== null &&
    rawGroup.quantity !== ''
  ) {
    normalized.quantity = rawGroup.quantity
  }

  if (
    rawGroup.__fixed_input_price !== undefined &&
    rawGroup.__fixed_input_price !== null
  ) {
    normalized.__fixed_input_price = rawGroup.__fixed_input_price
  }

  if (
    rawGroup.__fixed_output_price !== undefined &&
    rawGroup.__fixed_output_price !== null
  ) {
    normalized.__fixed_output_price = rawGroup.__fixed_output_price
  }

  return normalized
}

export const normalizeItemForUI = (item = {}) => {
  const price_groups =
    Array.isArray(item.price_groups) && item.price_groups.length > 0
      ? item.price_groups.map((pg) => normalizePriceGroup(pg, item))
      : [normalizePriceGroup(item, item)]

  return {
    ...item,
    price_groups
  }
}

// DistributionManager writes rows in configured column order. After a branch
// rename, live keys can have gaps (DK::0, DK::2, DK::3). Persist the config using
// the same consecutive occurrence indexes that history derives from those rows.
const normalizeSavedColumnMetadata = (metadata) => {
  const config = metadata.columnConfig
  if (!config?.columns || !config?.order) return metadata

  const columns = new Map(config.columns)
  const fields = new Map(config.customerHeaderFields || [])
  const keyMap = new Map()
  const idMap = new Map()
  const pairCounts = new Map()
  const savedFields = []

  config.order.forEach((id) => {
    const col = columns.get(id)
    if (!col || !['customer', 'black-circle'].includes(col.type)) return
    const oldKey = col.customerCode
    const { customerCode, branchCode } = ColumnManager.parseColumnKey(oldKey)
    const pair = `${customerCode}::${branchCode}`
    const index = pairCounts.get(pair) || 0
    pairCounts.set(pair, index + 1)
    const newKey = ColumnManager.buildColumnKey(customerCode, branchCode, index)
    keyMap.set(oldKey, newKey)
    idMap.set(id, id.startsWith('customer_') ? `customer_${newKey}` : id)
    savedFields.push([newKey, {
      ...fields.get(oldKey),
      edaban: branchCode,
      _customerCode: customerCode,
      _originalBranch: branchCode,
      _originalIndex: index
    }])
  })

  return {
    ...metadata,
    ...(metadata.hiddenColumnKeys && {
      hiddenColumnKeys: metadata.hiddenColumnKeys.map((key) => keyMap.get(key) ?? key)
    }),
    columnConfig: {
      ...config,
      columns: config.columns.map(([id, col]) => {
        if (!keyMap.has(col.customerCode)) return [id, col]
        const newId = idMap.get(id) ?? id
        return [newId, { ...col, id: newId, customerCode: keyMap.get(col.customerCode) }]
      }),
      widths: (config.widths || []).map(([id, width]) => [idMap.get(id) ?? id, width]),
      order: config.order.map((id) => idMap.get(id) ?? id),
      customerHeaderFields: savedFields
    }
  }
}

export function transformToApiFormat(currentItems, processed, metadata = {}) {
  const toNumber = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }

  const groupedByHinmei = currentItems.reduce((acc, item) => {
    const hinmei = item.hinmeiCode
    if (!acc[hinmei]) acc[hinmei] = []
    acc[hinmei].push(item)
    return acc
  }, {})

  const product_list = Object.entries(groupedByHinmei).map(
    ([hinmeiCode, items]) => {
      const productName = items[0]?.product_name || ''

      const niukabangoGroups = {}
      const niukabangoOrder = []

      // Group proxy items back into items
      const itemGroups = {}

      items.forEach((entry) => {
        const niukabango = String(
          entry._originalNiukabango || entry.niukabango || ''
        )
        const itemIdx = entry._itemIndex ?? 0
        const key = `${niukabango}___${itemIdx}`
        if (!itemGroups[key]) {
          itemGroups[key] = []
        }
        itemGroups[key].push(entry)
      })

      Object.entries(itemGroups).forEach(([key, proxyItems]) => {
        const firstProxy = proxyItems[0]
        const niukabango = String(
          firstProxy._originalNiukabango || firstProxy.niukabango || ''
        )

        const price_groups = proxyItems.map((proxy) => {
          const normalizedGroup = normalizePriceGroup(proxy, proxy)
          const quantityUnitVal = toNumber(normalizedGroup.quantity_unit)
          const quantityVal = toNumber(normalizedGroup.quantity)
          const boxCount =
            quantityUnitVal > 0 ? Math.floor(quantityVal / quantityUnitVal) : 0

          return {
            input_price: toNumber(normalizedGroup.input_price),
            output_price: toNumber(normalizedGroup.output_price),
            profit: normalizedGroup.profit,
            distribution_result: normalizedGroup.distribution_result,
            _priceId: normalizedGroup._priceId,
            __fixed_input_price: normalizedGroup.__fixed_input_price,
            __fixed_output_price: normalizedGroup.__fixed_output_price,
            shipping_mark: normalizedGroup.shipping_mark,
            grade: normalizedGroup.grade,
            size: normalizedGroup.size,
            remain_quantity: toNumber(normalizedGroup.remain_quantity),
            total_quantity: toNumber(normalizedGroup.total_quantity),
            package_quantity: toNumber(normalizedGroup.package_quantity),
            listing_quantity: toNumber(normalizedGroup.listing_quantity),
            quantity_unit: toNumber(normalizedGroup.quantity_unit),
            quantity: toNumber(normalizedGroup.quantity),
            box_count: boxCount
          }
        })

        const totalProfit = price_groups.reduce((sum, pg) => {
          const val = Number(pg.profit)
          return sum + (Number.isNaN(val) ? 0 : val)
        }, 0)

        const totalQuantity =
          price_groups.length === 1
            ? Number(price_groups[0]?.quantity || 0)
            : price_groups.reduce(
                (sum, pg) => sum + (Number(pg.total_quantity) || 0),
                0
              )

        const totalRemain = price_groups.reduce(
          (sum, pg) => sum + (Number(pg.remain_quantity) || 0),
          0
        )

        const cleanedItem = {
          _itemIndex: firstProxy._itemIndex ?? 0,
          grade: firstProxy.grade || '',
          package: firstProxy.package || '',
          product_name: firstProxy.product_name || '',
          production: firstProxy.production || '',
          quantity: totalQuantity,
          sanchi_code: firstProxy.sanchi_code,
          shipper_code: firstProxy.shipper_code,
          remain_quantity: totalRemain,
          shipping_mark: firstProxy.shipping_mark || '',
          size: firstProxy.size || '',
          weight: firstProxy.weight,
          nyuka_index: firstProxy.nyuka_index,
          receipt_number: firstProxy.receipt_number,
          quantity_unit: Number(firstProxy.quantity_unit) || 0,
          total_quantity: totalQuantity,
          package_quantity: Number(firstProxy.package_quantity) || 0,
          listing_quantity: Number(firstProxy.listing_quantity) || 0,
          input_price: price_groups[0]?.input_price,
          output_price: price_groups[0]?.output_price,
          profit: totalProfit,
          distribution_result: price_groups[0]?.distribution_result,

          price_groups
        }

        if (!niukabangoGroups[niukabango]) {
          niukabangoGroups[niukabango] = {
            items: [],
            niukabango,
            processed: firstProxy.processed
          }
          niukabangoOrder.push(niukabango)
        }
        niukabangoGroups[niukabango].items.push(cleanedItem)
        if (firstProxy.processed != null) {
          niukabangoGroups[niukabango].processed = firstProxy.processed
        }
      })

      const list_niukabango = niukabangoOrder.map((nk) => niukabangoGroups[nk])

      return {
        list_niukabango,
        meisei_hinmei_cd: hinmeiCode,
        product_name: productName
      }
    }
  )

  return {
    result: {
      data: {
        product_list,
        ui_metadata: normalizeSavedColumnMetadata(metadata)
      },
      timestamp: new Date().toISOString(),
      version: '1.0.3'
    },
    status: 'ok',
    confirmed: !!processed
  }
}

export const createEditPayload = (apiData, productNamesByCode = {}) => {
  if (!apiData?.result?.data?.product_list) return

  // Empty arrays are truthy in JavaScript. Treat a nyuka item as a popup row
  // only when its saved allocation contains at least one displayable row.
  const hasDisplayableRows = (item) => {
    const hasValues = (value) =>
      Array.isArray(value) ? value.length > 0 : !!value && Object.keys(value).length > 0

    return (
      hasValues(item?.distribution_result) ||
      hasValues(item?.price_groups)
    )
  }

  const inventoryData = {}
  const selectedNyukaNumbers = {}
  const restoredHeaderFields = {}

  apiData.result.data.product_list.forEach((product) => {
    ;(product.list_niukabango || []).forEach((nyuka) => {
      const rowsByProductCode = new Map()
      ;(nyuka.items || []).filter(hasDisplayableRows).forEach((item) => {
        // 品目 (MEISEI_HINMEI_CD) is the source of truth for the product
        // shown on each popup row. Fall back to the product/nyuka wrapper for
        // older output_data shapes that only store the code at that level.
        const code = String(
          item.meisei_hinmei_cd ||
            item.MEISEI_HINMEI_CD ||
            item.hinmeiCode ||
            nyuka.meisei_hinmei_cd ||
            product.meisei_hinmei_cd ||
            ''
        )
        if (!code) return

        const normalizedItem = normalizeItemForUI(item)
        const price_groups = normalizedItem.price_groups.map((p, idx) => ({
          ...p,
          _priceId: p._priceId || `p${idx}`
        }))
        const displayItem = {
          ...item,
          ...normalizedItem,
          hinmeiCode: code,
          product_name:
            item.product_name ||
            item.PRODUCT_NAME ||
            item.productName ||
            item.hinmeiName ||
            item.hinmei_name ||
            product.product_name ||
            product.PRODUCT_NAME ||
            product.productName ||
            product.hinmeiName ||
            product.hinmei_name ||
            productNamesByCode[code] ||
            '',
          stock_marker: item.stock_marker ?? nyuka.stock_marker,
          distribution_result: item.distribution_result,
          nyuka_index: item.nyuka_index,
          price_groups
        }

        if (!rowsByProductCode.has(code)) rowsByProductCode.set(code, [])
        rowsByProductCode.get(code).push(displayItem)

        ;(item.price_groups || []).forEach((priceItem) => {
          if (!Array.isArray(priceItem.distribution_result)) return
          const pairCount = {}
          priceItem.distribution_result.forEach((dist) => {
            const custCode = String(dist.customer_code ?? '')
            if (!custCode || custCode === 'price' || custCode === 'id') return
            const branchCode = String(dist.branch_code ?? '')
            const pairKey = `${custCode}::${branchCode}`
            const idx = pairCount[pairKey] ?? 0
            pairCount[pairKey] = idx + 1
            const columnKey = `${custCode}::${branchCode}::${idx}`

            if (!restoredHeaderFields[columnKey]) {
              restoredHeaderFields[columnKey] = {
                torioiki:
                  dist.reserve === 1
                    ? '●'
                    : dist.reserve === 2
                      ? '＋1日'
                      : dist.reserve === 3
                        ? '＋2日'
                        : '',
                basho: dist.location ?? '',
                edaban: branchCode,
                _customerCode: custCode,
                _originalBranch: branchCode,
                _originalIndex: idx
              }
            } else {
              const existing = restoredHeaderFields[columnKey]
              if (existing.torioiki === '' && dist.reserve > 0) {
                existing.torioiki =
                  dist.reserve === 1
                    ? '●'
                    : dist.reserve === 2
                      ? '＋1日'
                      : dist.reserve === 3
                        ? '＋2日'
                        : ''
              }
              if (existing.basho === '' && dist.location) {
                existing.basho = dist.location
              }
            }
          })
        })
      })

      rowsByProductCode.forEach((items, code) => {
        if (!inventoryData[code]) inventoryData[code] = []
        const existingNyuka = inventoryData[code].find(
          (entry) => String(entry.niukabango) === String(nyuka.niukabango)
        )
        if (existingNyuka) existingNyuka.items.push(...items)
        else {
          inventoryData[code].push({
            niukabango: nyuka.niukabango,
            stock_marker: nyuka.stock_marker,
            processed: nyuka.processed,
            items
          })
        }
        if (!selectedNyukaNumbers[code]) selectedNyukaNumbers[code] = []
        if (!selectedNyukaNumbers[code].some(
          (number) => String(number) === String(nyuka.niukabango)
        )) {
          selectedNyukaNumbers[code].push(nyuka.niukabango)
        }
      })
    })
  })

  const ui_metadata = apiData?.result?.data?.ui_metadata || {}

  return {
    inventoryData,
    selectedNyukaNumbers,
    restoredHeaderFields,
    ...ui_metadata
  }
}

export const getSafeProductName = (item) => {
  if (!item) return ''

  let rawName = item.product_list

  if (Array.isArray(rawName)) {
    return rawName
      .map((n) =>
        String(`${n.meisei_hinmei_cd} ${n.product_name}` || '').trim()
      )
      .filter(Boolean)
      .join(', ')
  }

  if (typeof rawName === 'string') {
    return rawName.trim()
  }

  return ''
}
